import React, { useState } from 'react';
import { CheckCircle2Icon } from 'lucide-react';
import { LeadWhatsAppHandoff } from '../LeadWhatsAppHandoff';
import { useStore } from '../../contexts/StoreContext';
import { Field } from '../ui/Field';
import { LeadProgress } from './StatusTracker';
import { downRanges, employmentOptions, seniorityOptions } from '../../data/options';
import { ContactPref, Lead } from '../../types/lead';
import { cleanText, isValidEmail, isValidName, isValidPhone, normalizePhone } from '../../utils/validation';
import { vehicleLabel } from '../../utils/catalog';
type Errors = Partial<Record<'name' | 'phone' | 'email' | 'downRange' | 'employment' | 'seniority' | 'consent', string>>;
export function CreditForm() {
  const {
    addLead,
    vehicles,
    brands
  } = useStore();
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    pref: 'whatsapp' as ContactPref,
    vehicleId: '',
    downRange: '',
    employment: '',
    seniority: '',
    consent: false,
    contact: true
  });
  const [errors, setErrors] = useState<Errors>({});
  const [lead, setLead] = useState<Lead | null>(null);
  const set = <K extends keyof typeof form,>(k: K, v: (typeof form)[K]) => setForm(f => ({
    ...f,
    [k]: v
  }));
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const er: Errors = {};
    if (!isValidName(form.name)) er.name = 'Ingresá tu nombre y apellido.';
    if (!isValidPhone(form.phone)) er.phone = 'Ingresá un teléfono válido con código de área.';
    if (form.email && !isValidEmail(form.email)) er.email = 'Ingresá un email válido.';
    if (!form.downRange) er.downRange = 'Elegí un rango de anticipo.';
    if (!form.employment) er.employment = 'Elegí tu situación laboral.';
    if (!form.seniority) er.seniority = 'Elegí tu antigüedad.';
    if (!form.consent) er.consent = 'Necesitamos tu consentimiento informado para continuar.';
    setErrors(er);
    if (Object.keys(er).length) return;
    const v = vehicles.find(x => x.id === form.vehicleId);
    setLead(addLead({
      channel: 'chequeo',
      name: cleanText(form.name, 80),
      phone: normalizePhone(form.phone),
      email: cleanText(form.email, 120),
      contactPref: form.pref,
      vehicleId: v?.id ?? null,
      vehicleLabel: v ? vehicleLabel(v, brands) : null,
      price: v?.price ?? null,
      down: null,
      term: null,
      simulation: null,
      message: '',
      credit: {
        downRange: form.downRange,
        employment: form.employment,
        seniority: form.seniority
      },
      consents: {
        dataProcessing: form.consent,
        contact: form.contact,
        at: new Date().toISOString()
      }
    }));
  };
  if (lead) {
    return <div role="status" data-id="element-465" data-mp="e8qiznmahogq">
        <CheckCircle2Icon className="h-7 w-7 text-ok" aria-hidden data-id="element-466" data-mp="e7xg4xqgbj3v" />
        <h2 className="mt-3 font-display text-2xl font-bold" data-id="element-467" data-mp="11yazlzuz09mu">Solicitud registrada</h2>
        <p className="mt-2 text-sm text-mist" data-id="element-468" data-mp="16p9jtaj2xrvd">
          Guardá tu número de seguimiento: <span className="font-mono text-base font-semibold text-white" data-id="element-469" data-mp="16rdymniiobaf">{lead.code}</span>. Un analista revisará tu información y la
          derivará a un asesor comercial. Esta solicitud no es una precalificación ni implica aprobación crediticia.
        </p>
        <LeadWhatsAppHandoff lead={lead} data-id="element-470" data-mp="314gezitomm6d" />
        <div className="mt-6 rounded-2xl border border-line bg-panel p-5" data-id="element-471" data-mp="2w6olwlhawu0l">
          <LeadProgress lead={lead} data-id="element-472" data-mp="2w7gf5pnfefnp" />
        </div>
      </div>;
  }
  const sel = (id: keyof Errors & keyof typeof form, label: string, opts: string[]) => <Field label={label} htmlFor={`cr-${id}`} error={errors[id]} data-id="element-473" data-mp="23j157hj5inj8">
      <select id={`cr-${id}`} className="input" value={String(form[id])} onChange={e => set(id, e.target.value as never)} data-id="element-474" data-mp="2hhsd2m7f7cyx">
        <option value="" data-id="element-475" data-mp="2dfxehpgvjdou">Seleccioná</option>
        {opts.map(o => <option key={o} value={o} data-id="element-476" data-mp="2dijm1p3rlt72">{o}</option>)}
      </select>
    </Field>;
  return <form onSubmit={onSubmit} noValidate className="space-y-4" data-id="element-477" data-mp="3sz0hh7i2gt33">
      <Field label="Nombre y apellido" htmlFor="cr-name" error={errors.name} data-id="element-478" data-mp="3oukh1lr82dv9">
        <input id="cr-name" className="input" value={form.name} onChange={e => set('name', e.target.value)} autoComplete="name" maxLength={80} data-id="element-479" data-mp="o0whwp7oambr" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-480" data-mp="s3jbovnwmhbl">
        <Field label="Teléfono / WhatsApp" htmlFor="cr-phone" error={errors.phone} data-id="element-481" data-mp="wzrklrbideab">
          <input id="cr-phone" className="input" inputMode="tel" value={form.phone} onChange={e => set('phone', e.target.value)} autoComplete="tel" maxLength={20} data-id="element-482" data-mp="1205w5jcbj6d3" />
        </Field>
        <Field label="Email (opcional)" htmlFor="cr-email" error={errors.email} data-id="element-483" data-mp="1b0c6wxgmts8k">
          <input id="cr-email" type="email" className="input" value={form.email} onChange={e => set('email', e.target.value)} autoComplete="email" maxLength={120} data-id="element-484" data-mp="1ay7vlugelqy9" />
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-485" data-mp="1ks866bugfoed">
        {sel('downRange', 'Anticipo disponible', downRanges)}
        {sel('employment', 'Situación laboral', employmentOptions)}
        {sel('seniority', 'Antigüedad laboral', seniorityOptions)}
        <Field label="Vehículo de interés (opcional)" htmlFor="cr-vehicle" data-id="element-486" data-mp="3f7nbi49sp9wv">
          <select id="cr-vehicle" className="input" value={form.vehicleId} onChange={e => set('vehicleId', e.target.value)} data-id="element-487" data-mp="3a6zn7qcyb76c">
            <option value="" data-id="element-488" data-mp="359he9zyxoryc">Todavía no lo decidí</option>
            {vehicles.filter(v => v.published).map(v => <option key={v.id} value={v.id} data-id="element-489" data-mp="30amsqq9l0paj">{vehicleLabel(v, brands)}</option>)}
          </select>
        </Field>
      </div>
      <fieldset data-id="element-490" data-mp="1tscp6pv435e">
        <legend className="label" data-id="element-491" data-mp="3sy0ju7qlmqmq">Medio de contacto preferido</legend>
        <div className="grid grid-cols-3 gap-2" data-id="element-492" data-mp="3sysg7ks0sse2">
          {(['whatsapp', 'llamada', 'email'] as ContactPref[]).map(p => <label key={p} className={`cursor-pointer rounded-xl border px-3 py-2.5 text-center text-sm font-medium transition-colors duration-150 ${form.pref === p ? 'border-neon bg-neon/10' : 'border-line text-mist hover:text-white'}`} data-id="element-493" data-mp="3j878n7udcdj0">
              <input type="radio" name="cr-pref" className="sr-only" checked={form.pref === p} onChange={() => set('pref', p)} data-id="element-494" data-mp="fqfmwb6tspt9" />
              {p === 'whatsapp' ? 'WhatsApp' : p === 'llamada' ? 'Llamada' : 'Email'}
            </label>)}
        </div>
      </fieldset>
      <div className="space-y-2 rounded-xl border border-line p-3" data-id="element-495" data-mp="209srvxasds40">
        <label className="flex items-start gap-3 text-xs text-mist" data-id="element-496" data-mp="207f140n53229">
          <input type="checkbox" checked={form.consent} onChange={e => set('consent', e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#1FA8FF]" data-id="element-497" data-mp="257b35uca0jf6" />
          <span data-id="element-498" data-mp="1va6i5xu62tzy">
            Presto mi consentimiento libre, expreso e informado para que VCARS trate los datos aquí ingresados con el único fin de evaluar alternativas de
            financiación y contactarme (Ley 25.326). No se consultarán bureaus de crédito sin mi autorización específica. Puedo ejercer mis derechos de acceso,
            rectificación y supresión en cualquier momento.
          </span>
        </label>
        <label className="flex items-start gap-3 text-xs text-mist" data-id="element-499" data-mp="3go16n5pbex53">
          <input type="checkbox" checked={form.contact} onChange={e => set('contact', e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#1FA8FF]" data-id="element-500" data-mp="150fkqdbrwnvv" />
          <span data-id="element-501" data-mp="102w3r5noxx08">Acepto ser contactado por un asesor comercial.</span>
        </label>
        {errors.consent && <p className="text-xs text-danger" role="alert" data-id="element-502" data-mp="1ers92uyd4vx5">{errors.consent}</p>}
      </div>
      <button type="submit" className="btn btn-primary h-12 w-full" data-id="element-503" data-mp="19yqsmiiicobg">Enviar solicitud de evaluación</button>
      <p className="text-[11px] text-mist" data-id="element-504" data-mp="1npcbi77yoy9c">
        Esta es una solicitud de evaluación preliminar realizada por un asesor. No es una precalificación automática ni garantiza la aprobación del crédito, que depende
        exclusivamente de la entidad otorgante.
      </p>
    </form>;
}